import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createPlan,
  updatePlan,
  getAllPlans,
  getPlanPlatformTypes,
  type CreatePlanPayload,
} from "../../services/subscriptionService";
import { Save, Loader2, RotateCcw } from "lucide-react";
import CustomSelect from "../../components/common/CustomSelect";

const DURATION_TYPES = ["months", "years"];
const CURRENCY_OPTIONS = ["INR", "USD", "GBP"];

const PLATFORM_OPTIONS = [
  { value: "hospital", label: "Hospital" },
  { value: "doctor", label: "Doctor" },
  { value: "public", label: "Public User" },
];

const INITIAL_FORM: CreatePlanPayload = {
  planName: "",
  price: 0,
  currency: "INR",
  description: "",
  durationType: "months",
  durationValue: 1,
  includedFeatures: [],
  planTag: "",
  caseLimit: null,
  userLimit: null,
  platformTypes: ["hospital"],
  isVisible: true,
  isActive: true,
};

const SubscriptionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);

  const platformParam = searchParams.get("platform");
  const defaultPlatform = PLATFORM_OPTIONS.some((o) => o.value === platformParam)
    ? (platformParam as string)
    : "hospital";

  const [form, setForm] = useState<CreatePlanPayload>(() => ({
    ...INITIAL_FORM,
    platformTypes: [defaultPlatform],
  }));
  const [savedForm, setSavedForm] = useState<CreatePlanPayload>(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [featuresText, setFeaturesText] = useState("");

  const selectedPlatformCount = form.platformTypes?.length ?? 0;
  const allPlatformsSelected = selectedPlatformCount === PLATFORM_OPTIONS.length;
  const somePlatformsSelected = selectedPlatformCount > 0 && !allPlatformsSelected;
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePlatformsSelected;
    }
  }, [somePlatformsSelected]);

  useEffect(() => {
    if (!isEdit || !id) return;
    getAllPlans()
      .then((plans) => {
        const plan = plans.find((p) => p.id === id);
        if (!plan) {
          toast.error("Subscription plan not found");
          navigate("/super-admin/subscription");
          return;
        }
        const data: CreatePlanPayload = {
          planName: plan.planName,
          price: plan.price,
          currency: plan.currency || "INR",
          description: plan.description || "",
          durationType: plan.durationType || "months",
          durationValue: plan.durationValue ?? 1,
          includedFeatures: plan.includedFeatures?.length ? plan.includedFeatures : [],
          planTag: plan.planTag || "",
          caseLimit: plan.caseLimit ?? null,
          userLimit: plan.userLimit ?? null,
          platformTypes: getPlanPlatformTypes(plan),
          isVisible: plan.isVisible,
          isActive: plan.isActive,
        };
        setForm(data);
        setSavedForm(data);
        setFeaturesText((plan.includedFeatures || []).join("\n"));
      })
      .catch(() => {
        toast.error("Failed to load subscription plan");
        navigate("/super-admin/subscription");
      })
      .finally(() => setFetching(false));
  }, [id, isEdit, navigate]);

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const setPlatformTypes = (next: string[]) => {
    setForm((prev) => ({ ...prev, platformTypes: next }));
    clearError("platformTypes");
  };

  const isPlatformChecked = (platform: string) =>
    form.platformTypes?.includes(platform) ?? false;

  const togglePlatform = (platform: string) => {
    const current = form.platformTypes || [];
    const next = current.includes(platform)
      ? current.filter((p) => p !== platform)
      : [...current, platform];
    setPlatformTypes(next);
  };

  const toggleSelectAllPlatforms = () => {
    setPlatformTypes(allPlatformsSelected ? [] : PLATFORM_OPTIONS.map((o) => o.value));
  };

  const handleReset = () => {
    setForm(isEdit ? savedForm : { ...INITIAL_FORM, platformTypes: [defaultPlatform] });
    setFeaturesText(isEdit ? (savedForm.includedFeatures || []).join("\n") : "");
    setErrors({});
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!form.planName?.trim()) errs.planName = "Plan name is required";
    if (form.price === undefined || form.price === null || Number(form.price) < 0) errs.price = "Price is required";
    if (!form.currency?.trim()) errs.currency = "Currency is required";
    if (form.durationValue === undefined || form.durationValue === null || Number(form.durationValue) < 1) errs.durationValue = "Duration value is required";
    if (!form.durationType?.trim()) errs.durationType = "Duration type is required";
    if (!form.platformTypes || form.platformTypes.length === 0) errs.platformTypes = "Select at least one platform";

    const includedFeatures = featuresText
      .split(/[\n,]/)
      .map((f) => f.trim())
      .filter(Boolean);
    if (!includedFeatures.length) errs.includedFeatures = "At least one feature is required";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const includedFeatures = featuresText
      .split(/[\n,]/)
      .map((f) => f.trim())
      .filter(Boolean);

    const payload: CreatePlanPayload = {
      planName: form.planName.trim(),
      price: Number(form.price),
      currency: (form.currency || "INR").trim(),
      description: (form.description || "").trim(),
      durationType: form.durationType,
      durationValue: Number(form.durationValue),
      includedFeatures,
      planTag: (form.planTag || "").trim() || null,
      caseLimit: form.caseLimit === null || form.caseLimit === undefined ? null : Number(form.caseLimit),
      userLimit: form.userLimit === null || form.userLimit === undefined ? null : Number(form.userLimit),
      platformTypes: form.platformTypes,
      isVisible: !!form.isVisible,
      isActive: true,
    };

    setLoading(true);
    try {
      if (isEdit && id) {
        await updatePlan(id, payload);
        toast.success("Plan updated successfully");
      } else {
        await createPlan(payload);
        toast.success("Plan created successfully");
      }
      navigate("/super-admin/subscription");
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Operation failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500">
        <button onClick={() => navigate("/super-admin/subscription")} className="hover:text-gray-900 transition-colors">
          Subscriptions
        </button>
        <span className="text-gray-400">/</span>
        <span className="text-gray-900 font-medium">{isEdit ? "Edit" : "Add New"}</span>
      </nav>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-5">
          {/* Platform Availability */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Platform Availability</h2>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allPlatformsSelected}
                  onChange={toggleSelectAllPlatforms}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Select All
              </label>
              <span className="w-px h-5 bg-gray-200 self-center" />
              {PLATFORM_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isPlatformChecked(option.value)}
                    onChange={() => togglePlatform(option.value)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {errors.platformTypes && <p className="text-xs text-red-600 mt-1">{errors.platformTypes}</p>}
          </div>

          <hr className="border-gray-100" />

          {/* Plan Details */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Plan Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Plan Name" name="planName" value={form.planName || ""} onChange={handleChange} error={errors.planName} required placeholder="e.g. Premium Plan" />
              <FormField label="Plan Tag" name="planTag" value={form.planTag || ""} onChange={handleChange} error={errors.planTag} placeholder="e.g. Most Popular" />
              <FormField
                label="Price"
                name="price"
                value={String(form.price ?? "")}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setForm((prev) => ({ ...prev, price: Number.isNaN(value) ? 0 : value }));
                  clearError("price");
                }}
                error={errors.price}
                required
                type="number"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency <span className="text-red-500 ml-0.5">*</span>
                </label>
                <CustomSelect
                  value={form.currency || "INR"}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, currency: val }));
                    clearError("currency");
                  }}
                  options={CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))}
                />
                {errors.currency && <p className="text-xs text-red-600 mt-1">{errors.currency}</p>}
              </div>
              <div className="sm:col-span-2">
                <FormField label="Description" name="description" value={form.description || ""} onChange={handleChange} error={errors.description} placeholder="Short description of the plan" />
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Limits & Duration */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Limits & Duration</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField
                label="Case Limit"
                name="caseLimit"
                value={form.caseLimit === null || form.caseLimit === undefined ? "" : String(form.caseLimit)}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, caseLimit: e.target.value === "" ? null : Number(e.target.value) }));
                }}
                type="number"
                min={0}
                placeholder="Unlimited"
              />
              <FormField
                label="User Limit"
                name="userLimit"
                value={form.userLimit === null || form.userLimit === undefined ? "" : String(form.userLimit)}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, userLimit: e.target.value === "" ? null : Number(e.target.value) }));
                }}
                type="number"
                min={0}
                placeholder="Unlimited"
              />
              <FormField
                label="Duration Value"
                name="durationValue"
                value={String(form.durationValue ?? "")}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setForm((prev) => ({ ...prev, durationValue: Number.isNaN(value) ? 1 : value }));
                  clearError("durationValue");
                }}
                error={errors.durationValue}
                required
                type="number"
                min={1}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration Type <span className="text-red-500 ml-0.5">*</span>
                </label>
                <CustomSelect
                  value={form.durationType || "months"}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, durationType: val }));
                    clearError("durationType");
                  }}
                  options={DURATION_TYPES.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
                />
                {errors.durationType && <p className="text-xs text-red-600 mt-1">{errors.durationType}</p>}
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Included Features */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Included Features</h2>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Features <span className="text-red-500 ml-0.5">*</span>
            </label>
            <textarea
              value={featuresText}
              onChange={(e) => {
                setFeaturesText(e.target.value);
                clearError("includedFeatures");
              }}
              placeholder="One feature per line or comma-separated"
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-y ${
                errors.includedFeatures ? "border-red-300 bg-red-50" : "border-gray-200"
              }`}
            />
            {errors.includedFeatures && <p className="text-xs text-red-600 mt-1">{errors.includedFeatures}</p>}
          </div>

          <hr className="border-gray-100" />

          {/* Visibility */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Visibility</h2>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, isVisible: !prev.isVisible }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  form.isVisible ? "bg-primary-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                    form.isVisible ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
              <span className="ml-3 text-sm text-gray-600">{form.isVisible ? "Visible to subscribers" : "Hidden from subscribers"}</span>
            </div>
          </div>

          {/* Actions */}
          <hr className="border-gray-100" />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={() => navigate("/super-admin/subscription")}
              className="px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? "Update Plan" : "Create Plan"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// --- Helper Component ---

const FormField = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  type = "text",
  min,
  placeholder,
  className,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  type?: string;
  min?: number;
  placeholder?: string;
  className?: string;
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      min={min}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
        error ? "border-red-300 bg-red-50" : "border-gray-200"
      }`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

export default SubscriptionForm;